import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { alpha } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";

/**
 * Modal estándar de la web (mismo diseño que "Información Cliente" en Préstamos › Abonos por fecha):
 *
 *   ┌───────────────────────────────────────────┐
 *   │ Título (fondo primario)               [X] │  ← encabezado fijo
 *   ├───────────────────────────────────────────┤
 *   │ Contenido con scroll                      │  ← StandardModalBody
 *   ├───────────────────────────────────────────┤
 *   │ [Cancelar]                     [Guardar]  │  ← StandardModalFooter (fijo)
 *   └───────────────────────────────────────────┘
 *
 * Uso simple (el contenido va al cuerpo y `actions` al pie):
 *
 *   <StandardModal open={open} onClose={close} title="Editar" actions={<>
 *     <Button variant="outlined" onClick={close}>Cancelar</Button>
 *     <Button variant="contained" onClick={save}>Guardar</Button>
 *   </>}>
 *     ...campos...
 *   </StandardModal>
 *
 * Uso compuesto (un componente que arma su propio cuerpo y pie, p. ej. un formulario que
 * también se usa como página): `composed` y adentro <StandardModalBody> / <StandardModalFooter>.
 *
 * Cargando: el modal muestra su propio "Cargando…" sobre el contenido mientras se abre y
 * mientras `loading` sea true, y lo mantiene LOADING_TAIL_MS (2 s) después. Para conectarlo
 * con el contenido use `useModalLoading()` y pase sus showLoading / hideLoading al hijo:
 *
 *   const ml = useModalLoading();
 *   <StandardModal ... loading={ml.loading}>
 *     <Hijo showLoading={ml.showLoading} hideLoading={ml.hideLoading} />
 *   </StandardModal>
 *
 * (El "Cargando" general de la app queda detrás de los modales, por eso cada modal tiene el suyo.)
 */

/** Tiempo extra que se mantiene el "Cargando…" después de terminar de cargar. */
export const LOADING_TAIL_MS = 2000;

/** Estado de carga para un modal: cuenta llamadas anidadas de show/hide. */
export const useModalLoading = () => {
  const [count, setCount] = useState(0);
  const showLoading = useCallback(() => setCount((c) => c + 1), []);
  const hideLoading = useCallback(
    () => setCount((c) => Math.max(0, c - 1)),
    [],
  );
  return { loading: count > 0, showLoading, hideLoading };
};
export interface StandardModalProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** Texto pequeño bajo el título, dentro del encabezado. */
  subtitle?: React.ReactNode;
  /** Ancho máximo en px (por defecto 800). En pantallas pequeñas ocupa el 95 %. */
  maxWidth?: number;
  /** Alto fijo opcional; por defecto se ajusta al contenido hasta el 90 % de la pantalla. */
  height?: number | string;
  /** Botones del pie (solo en uso simple). */
  actions?: React.ReactNode;
  /** El hijo arma su propio StandardModalBody / StandardModalFooter. */
  composed?: boolean;
  /** Evita cerrar con clic afuera o Escape (p. ej. mientras se guarda). */
  disableClose?: boolean;
  /** Muestra "Cargando…" sobre el contenido (ver useModalLoading). */
  loading?: boolean;
  children: React.ReactNode;
}

const paperSx = (
  maxWidth: number,
  height?: number | string,
): SxProps<Theme> => ({
  position: "absolute",
  // En teléfono ocupa toda la pantalla (más espacio para formularios y tablas); desde sm, centrado.
  top: { xs: 0, sm: "50%" },
  left: { xs: 0, sm: "50%" },
  transform: { xs: "none", sm: "translate(-50%, -50%)" },
  width: { xs: "100%", sm: "90%" },
  maxWidth: { xs: "100%", sm: maxWidth },
  height: { xs: "100dvh", sm: height ?? "auto" },
  maxHeight: { xs: "100dvh", sm: "90vh" },
  bgcolor: "background.paper",
  borderRadius: { xs: 0, sm: 2 },
  // Respeta la muesca y la barra inferior del teléfono.
  pt: { xs: "env(safe-area-inset-top)", sm: 0 },
  pb: { xs: "env(safe-area-inset-bottom)", sm: 0 },
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  outline: "none",
});

/** Cuerpo con scroll del modal estándar. */
export const StandardModalBody: React.FC<{
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}> = ({ children, sx }) => (
  <Box
    sx={{
      p: 3,
      overflowY: "auto",
      flexGrow: 1,
      minHeight: 0,
      ...(sx as object),
    }}
  >
    {children}
  </Box>
);

/** Pie fijo del modal estándar: el primer botón queda a la izquierda y el último a la derecha. */
export const StandardModalFooter: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 1,
      flexWrap: "wrap",
      p: 2,
      borderTop: 1,
      borderColor: "divider",
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const StandardModal: React.FC<StandardModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  maxWidth = 800,
  height,
  actions,
  composed = false,
  disableClose = false,
  loading = false,
  children,
}) => {
  // Visible al abrir (mientras renderiza), mientras `loading` y LOADING_TAIL_MS después.
  const [opening, setOpening] = useState(false);
  const [tail, setTail] = useState(false);
  const tailTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // useLayoutEffect: el "Cargando" ya sale en el primer pintado. Se usa un timeout (no rAF) para
  // que React no junte el true/false en el mismo render y el overlay sí alcance a mostrarse.
  useLayoutEffect(() => {
    if (!open) return undefined;
    setOpening(true);
    const timer = setTimeout(() => setOpening(false), 50);
    return () => clearTimeout(timer);
  }, [open]);

  const busy = open && (opening || loading);
  useEffect(() => {
    if (tailTimer.current) clearTimeout(tailTimer.current);
    if (busy) {
      setTail(true);
      return undefined;
    }
    tailTimer.current = setTimeout(() => setTail(false), LOADING_TAIL_MS);
    return () => {
      if (tailTimer.current) clearTimeout(tailTimer.current);
    };
  }, [busy]);

  const showOverlay = open && (busy || tail);

  return (
    <Modal
      open={open}
      onClose={disableClose ? undefined : onClose}
      aria-labelledby="standard-modal-title"
    >
      <Paper
        sx={{ ...(paperSx(maxWidth, height) as object), position: "absolute" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            p: 2,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            flexShrink: 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography id="standard-modal-title" variant="h6" noWrap>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            disabled={disableClose}
            sx={{ color: "primary.contrastText" }}
            aria-label="cerrar"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            minHeight: 0,
          }}
        >
          {composed ? (
            children
          ) : (
            <>
              <StandardModalBody>{children}</StandardModalBody>
              {actions && <StandardModalFooter>{actions}</StandardModalFooter>}
            </>
          )}
          {showOverlay && (
            <Box
              role="progressbar"
              aria-label="Cargando"
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                // Velo sobre el contenido mientras carga, del color de la superficie en cada modo.
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.85),
                minHeight: 160,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Cargando…
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Modal>
  );
};

export default StandardModal;
